// Condition-tracking engine — the computational half of the tracking feature.
// ---------------------------------------------------------------------------
// Pure functions over a small, source-agnostic input (TrackInput). Adapters in
// adapters.ts build that input from the app's PHR or from the physician-demo
// dataset; nothing here touches the DOM, Svelte, Firebase or either schema, so
// the rules are unit-tested in plain Node (collate.test.ts) and shared by the
// patient app and the admin console's tracker. The rules are deliberately small:
//
//   inTarget     the measure's target predicate on one value
//   attention    the measure's "needs attention" threshold on one value
//   trend        last two readings vs the two before, direction-aware, with a
//                per-measure tolerance so noise reads as "stable"
//   status       controlled / improving / above-target / needs-attention /
//                monitoring, decided on the condition's PRIMARY measure
//   careHistory  medication changes, hypoglycaemia, screening, and the dated
//                milestones where the primary measure crossed its target
import type { ConditionSpec, MeasureSpec, Target, Attention, Direction } from './spec.ts';
import { CONDITIONS, classOf } from './spec.ts';

// ── input contract (what an adapter must provide) ─────────────────────────────
export interface Reading { date: string; value: number; ref?: number }           // date = ISO YYYY-MM-DD
export interface CurrentMedication { name: string; class?: string; dose?: string; since?: string }
export interface CourseSegment { start: string; end: string | null; dose: string }
export interface MedicationCourse { drug: string; class: string; start: string; end: string | null; segments: CourseSegment[] }
export type MedAction = 'start' | 'increase' | 'decrease' | 'change' | 'stop';
export interface MedicationEvent { date: string; drug: string; action: MedAction; dose: string | null; reason: string }
export interface HypoEvent { date: string; glucose: number; context: string; severity: string; medication: string }
export interface ScreeningEntry { date: string; result: string }
export interface TrackedCondition { description: string; status?: string; since?: string }
export interface TrackInput {
  readings: (m: MeasureSpec) => Reading[];
  currentMedications: CurrentMedication[];
  courses: MedicationCourse[];
  events: MedicationEvent[];
  hypoglycemia: HypoEvent[];
  screening: Record<string, ScreeningEntry[]>;
  /** The record's own condition list; when given, only specs matching one of them are collated. */
  conditions?: TrackedCondition[];
}

// ── output ────────────────────────────────────────────────────────────────────
export interface Point { date: string; value: number; visitIndex?: number; inTarget: boolean | null; attention: boolean }
export type Trend = 'improving' | 'worsening' | 'stable' | 'insufficient';
export interface MeasureTrack {
  spec: MeasureSpec;
  points: Point[];
  latest: Point | null;
  previous: Point | null;
  delta: number | null;             // latest − previous
  trend: Trend;
  atGoal: boolean | null;           // latest in target (null = no target / no data)
  attention: boolean;               // latest beyond the attention threshold
  consecutiveAtGoal: number;        // trailing run of in-target points
  atGoalPct12m: number | null;      // share of points in the 12 months before latest that were in target
  min: Point | null;
  max: Point | null;
  firstAtGoal: Point | null;        // first point ever in target
}
export type ConditionStatus = 'controlled' | 'improving' | 'above-target' | 'needs-attention' | 'monitoring';
export type CareKind = 'medication' | 'hypoglycemia' | 'screening' | 'milestone';
export interface CareEvent {
  date: string; kind: CareKind; title: string; detail: string; severity: 'info' | 'warn' | 'danger' | 'good';
  /** Structured form of the title for localisation (lib/i18n careText); title/detail stay English. */
  key?: 'med' | 'hypo' | 'screen' | 'reached' | 'left' | 'first' | 'attention';
  params?: Record<string, string | number | null>;
}
export interface ConditionTrack {
  spec: ConditionSpec;
  condition: TrackedCondition | null;   // the record's condition this track was matched to
  status: ConditionStatus;
  statusReason: string;
  primary: MeasureTrack | null;
  measures: MeasureTrack[];
  courses: MedicationCourse[];
  events: MedicationEvent[];
  currentTreatment: CurrentMedication[];
  untreated: boolean;               // expectsTreatment && nothing from treatmentClasses on the current list
  careHistory: CareEvent[];         // newest first
  outOfTargetVisits: number;        // readings where the primary measure was out of target
}

// ── primitives ────────────────────────────────────────────────────────────────
export function inTarget(t: Target | undefined, v: number): boolean | null {
  if (!t) return null;
  switch (t.kind) {
    case 'below': return v < (t.value as number);
    case 'atOrBelow': return v <= (t.value as number);
    case 'above': return v > (t.value as number);
    case 'atOrAbove': return v >= (t.value as number);
    case 'range': return v >= (t.low as number) && v <= (t.high as number);
  }
}

export function isAttention(a: Attention | undefined, v: number): boolean {
  if (!a) return false;
  if (a.kind === 'above') return v > a.value;
  if (a.kind === 'atOrAbove') return v >= a.value;
  return v < a.value;
}

const mean = (xs: number[]) => xs.reduce((s, x) => s + x, 0) / xs.length;

/** Signed "better-ness" of a change for a direction: positive = improving. */
function betterness(direction: Direction, from: number, to: number, target?: Target): number {
  if (direction === 'lowerBetter') return from - to;
  if (direction === 'higherBetter') return to - from;
  if (target && target.kind === 'range') {                     // range: moving toward the centre
    const c = ((target.low as number) + (target.high as number)) / 2;
    return Math.abs(from - c) - Math.abs(to - c);
  }
  return 0;
}

/** Trend from the last two points vs the two before (or what exists of them). */
export function trendOf(points: Point[], spec: MeasureSpec): Trend {
  const n = points.length;
  if (n < 2) return 'insufficient';
  const recent = points.slice(Math.max(0, n - 2)).map((p) => p.value);
  const before = points.slice(Math.max(0, n - 4), n - 2).map((p) => p.value);
  if (before.length === 0) return 'insufficient';
  const b = betterness(spec.direction, mean(before), mean(recent), spec.target);
  if (Math.abs(b) < spec.tolerance) return 'stable';
  return b > 0 ? 'improving' : 'worsening';
}

const DAY = 86400000;
const ms = (iso: string) => Date.parse(iso);

export function trackMeasure(readings: Reading[], spec: MeasureSpec): MeasureTrack {
  const points: Point[] = readings
    .filter((r) => Number.isFinite(r.value) && !Number.isNaN(ms(r.date)))
    .map((r) => ({ date: r.date, value: r.value, visitIndex: r.ref, inTarget: inTarget(spec.target, r.value), attention: isAttention(spec.attention, r.value) }))
    .sort((a, b) => ms(a.date) - ms(b.date));
  const latest = points[points.length - 1] ?? null;
  const previous = points[points.length - 2] ?? null;
  let consecutiveAtGoal = 0;
  for (let i = points.length - 1; i >= 0 && points[i].inTarget === true; i--) consecutiveAtGoal++;
  let atGoalPct12m: number | null = null;
  if (latest && spec.target) {
    const win = points.filter((p) => ms(latest.date) - ms(p.date) <= 365 * DAY);
    atGoalPct12m = Math.round((100 * win.filter((p) => p.inTarget).length) / win.length);
  }
  let min: Point | null = null, max: Point | null = null;
  for (const p of points) { if (!min || p.value < min.value) min = p; if (!max || p.value > max.value) max = p; }
  return {
    spec, points, latest, previous,
    delta: latest && previous ? Number((latest.value - previous.value).toFixed(spec.decimals + 1)) : null,
    trend: trendOf(points, spec),
    atGoal: latest ? latest.inTarget : null,
    attention: latest ? latest.attention : false,
    consecutiveAtGoal, atGoalPct12m, min, max,
    firstAtGoal: points.find((p) => p.inTarget === true) ?? null,
  };
}

// ── status ────────────────────────────────────────────────────────────────────
export const fmtValue = (v: number, s: MeasureSpec) => `${v.toFixed(s.decimals)}${s.unit === '%' ? '' : ' '}${s.unit}`.trim();

export function statusOf(primary: MeasureTrack | null, untreated: boolean): { status: ConditionStatus; reason: string } {
  if (!primary || !primary.latest) return { status: 'monitoring', reason: 'No numeric measure decides this condition; see screening and care history.' };
  const s = primary.spec;
  const latest = primary.latest;
  const tgt = s.target ? ` (target ${s.target.label})` : '';
  const noRx = ' and no treatment is on the current medication list';
  if (primary.attention) {
    const why = `${s.label} ${fmtValue(latest.value, s)} is ${s.attention?.label ?? 'beyond the attention threshold'}`;
    return { status: 'needs-attention', reason: untreated ? `${why}${noRx}.` : `${why}.` };
  }
  if (primary.atGoal == null) return { status: 'monitoring', reason: `${s.label} ${fmtValue(latest.value, s)}; no target defined.` };
  if (primary.atGoal) {
    if (primary.consecutiveAtGoal >= 2) return { status: 'controlled', reason: `${s.label} ${fmtValue(latest.value, s)} at goal${tgt} for ${primary.consecutiveAtGoal} consecutive readings.` };
    return { status: 'improving', reason: `${s.label} ${fmtValue(latest.value, s)} just reached goal${tgt}; one reading so far.` };
  }
  if (primary.trend === 'worsening') {
    const why = `${s.label} ${fmtValue(latest.value, s)} is above goal${tgt} and worsening`;
    return { status: 'needs-attention', reason: untreated ? `${why}, with no treatment on the current medication list.` : `${why}.` };
  }
  if (primary.trend === 'improving') return { status: 'improving', reason: `${s.label} ${fmtValue(latest.value, s)} is above goal${tgt} but improving.` };
  return { status: 'above-target', reason: `${s.label} ${fmtValue(latest.value, s)} is above goal${tgt}${untreated ? '; no treatment on the current medication list' : ''}.` };
}

// ── condition ─────────────────────────────────────────────────────────────────
const startsWithAny = (name: string, prefixes: string[]) => prefixes.some((p) => (name || '').toLowerCase().startsWith(p.toLowerCase()));
const classMatches = (cls: string, classes: string[]) => classes.some((c) => (cls || '').toLowerCase().includes(c.toLowerCase()));
const treats = (name: string, cls: string | undefined, spec: ConditionSpec) =>
  startsWithAny(name, spec.medications) || classMatches(cls ?? classOf(name), spec.treatmentClasses);

export function trackCondition(input: TrackInput, spec: ConditionSpec, condition: TrackedCondition | null = null): ConditionTrack {
  const measures = spec.measures.map((m) => trackMeasure(input.readings(m), m));
  const primary = spec.primary ? measures.find((m) => m.spec.key === spec.primary) ?? null : null;
  const currentTreatment = input.currentMedications.filter((m) => treats(m.name, m.class, spec));
  const untreated = spec.expectsTreatment && currentTreatment.length === 0;
  const { status, reason } = statusOf(primary, untreated);

  const courses = input.courses.filter((c) => treats(c.drug, c.class, spec));
  const events = input.events.filter((e) => treats(e.drug, undefined, spec));

  const care: CareEvent[] = [];
  const VERB: Record<MedAction, string> = { start: 'Started', stop: 'Stopped', increase: 'Increased', decrease: 'Reduced', change: 'Changed' };
  for (const e of events) care.push({ date: e.date, kind: 'medication', title: `${VERB[e.action]} ${e.drug}${e.dose ? ` — ${e.dose}` : ''}`, detail: e.reason, severity: e.action === 'stop' ? 'warn' : 'info', key: 'med', params: { action: e.action, drug: e.drug, dose: e.dose } });
  if (spec.includeHypoglycemia) for (const h of input.hypoglycemia) care.push({ date: h.date, kind: 'hypoglycemia', title: `Hypoglycaemia ${h.glucose} mg/dL${h.severity ? ` (${h.severity})` : ''}`, detail: `${h.context}${h.medication ? ` On: ${h.medication}.` : ''}`, severity: 'warn', key: 'hypo', params: { glucose: h.glucose, severity: h.severity } });
  for (const k of spec.screening) for (const s of input.screening[k] ?? []) care.push({ date: s.date, kind: 'screening', title: screeningTitle(k), detail: s.result, severity: 'info', key: 'screen', params: { stream: k } });
  if (primary && primary.spec.target) {
    const s = primary.spec;
    let prev: boolean | null = null;                            // every crossing of the target boundary
    for (const p of primary.points) {
      if (prev !== null && p.inTarget !== prev) {
        care.push(p.inTarget
          ? { date: p.date, kind: 'milestone', title: `${s.label} reached goal`, detail: `${fmtValue(p.value, s)} (target ${s.target!.label}).`, severity: 'good', key: 'reached', params: { measure: s.key, label: s.label, value: fmtValue(p.value, s), target: s.target!.label } }
          : { date: p.date, kind: 'milestone', title: `${s.label} left goal`, detail: `${fmtValue(p.value, s)} (target ${s.target!.label}).`, severity: 'warn', key: 'left', params: { measure: s.key, label: s.label, value: fmtValue(p.value, s), target: s.target!.label } });
      }
      prev = p.inTarget;
    }
    if (primary.points.length) {
      const first = primary.points[0];
      care.push({ date: first.date, kind: 'milestone', title: `First ${s.label} on record`, detail: `${fmtValue(first.value, s)}${first.inTarget === false ? ' — above goal at onset' : ''}.`, severity: first.inTarget === false ? 'warn' : 'info', key: 'first', params: { measure: s.key, label: s.label } });
    }
    if (primary.attention && primary.latest) care.push({ date: primary.latest.date, kind: 'milestone', title: `${s.label} needs attention`, detail: reason, severity: 'danger', key: 'attention', params: { measure: s.key, label: s.label } });
  }
  care.sort((a, b) => ms(b.date) - ms(a.date) || a.kind.localeCompare(b.kind));

  return {
    spec, condition, status, statusReason: reason, primary, measures, courses, events, currentTreatment, untreated,
    careHistory: care,
    outOfTargetVisits: primary ? primary.points.filter((p) => p.inTarget === false).length : 0,
  };
}

export function screeningTitle(key: string): string {
  return ({ retinal: 'Retinal screening', foot: 'Foot examination', liverUltrasound: 'Liver ultrasound', homeBloodPressure: 'Home blood-pressure log' } as Record<string, string>)[key] ?? key;
}

/** Collate every applicable spec. With `input.conditions`, a spec applies only when it matches one of them. */
export function collate(input: TrackInput, specs: ConditionSpec[] = CONDITIONS): ConditionTrack[] {
  const out: ConditionTrack[] = [];
  for (const spec of specs) {
    if (input.conditions) {
      const match = input.conditions.find((c) => spec.match.test(c.description));
      if (!match) continue;
      out.push(trackCondition(input, spec, match));
    } else out.push(trackCondition(input, spec));
  }
  return out;
}

/** The track for one named condition (e.g. a Wellness card), or null when no spec covers it. */
export function trackFor(input: TrackInput, description: string, specs: ConditionSpec[] = CONDITIONS): ConditionTrack | null {
  const spec = specs.find((s) => s.match.test(description || ''));
  return spec ? trackCondition(input, spec, { description }) : null;
}

export const STATUS_ORDER: ConditionStatus[] = ['needs-attention', 'above-target', 'improving', 'controlled', 'monitoring'];
export const STATUS_LABEL: Record<ConditionStatus, string> = {
  'needs-attention': 'Needs attention', 'above-target': 'Above target', improving: 'Improving', controlled: 'Controlled', monitoring: 'Monitoring',
};

/** Tracks ordered by urgency, then by spec order. */
export function byUrgency(tracks: ConditionTrack[]): ConditionTrack[] {
  return [...tracks].sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
}
